import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { NavLink, Outlet, useParams, Link } from 'react-router-dom';
import s from './AdminLayout.module.css';

// ---------------------------------------------------------------------------
// AdminContext – makes the secret available to all admin pages
// ---------------------------------------------------------------------------

interface AdminContextValue {
  secret: string;
  /** Helper to build absolute admin paths */
  adminPath: (sub?: string) => string;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    throw new Error('useAdmin must be used within an AdminLayout');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Navigation items
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { label: 'Dashboard', to: '',        icon: '\u25A3' },  // ▣
  { label: 'Tenants',   to: 'tenants', icon: '\u2302' },  // ⌂
  { label: 'Users',     to: 'users',   icon: '\u263A' },  // ☺
  { label: 'Config',    to: 'config',  icon: '\u2699' },  // ⚙
  { label: 'Audit Log', to: 'audit',   icon: '\u2630' },  // ☰
];

// ---------------------------------------------------------------------------
// AdminLayout
// ---------------------------------------------------------------------------

export default function AdminLayout() {
  const { secret } = useParams<{ secret: string }>();

  if (!secret) {
    return <div style={{ padding: 'var(--space-6)', color: 'var(--danger-text)' }}>Missing admin secret.</div>;
  }

  const basePath = `/__admin/${secret}`;
  const adminPath = (sub?: string) => (sub ? `${basePath}/${sub}` : basePath);

  const ctxValue: AdminContextValue = { secret, adminPath };

  return (
    <AdminContext.Provider value={ctxValue}>
      <div className={s.layout}>
        {/* Sidebar */}
        <aside className={s.sidebar}>
          <div className={s.sidebarHeader}>
            <span className={s.sidebarLogo}>DMARCopolo</span>
            <span className={s.adminBadge}>Admin</span>
          </div>

          <nav className={s.nav}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={adminPath(item.to)}
                end={item.to === ''}
                className={({ isActive }) => (isActive ? s.navItemActive : s.navItem)}
              >
                <span className={s.navIcon}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={s.sidebarFooter}>
            <Link to="/app" className={s.backLink}>
              &larr; Back to App
            </Link>
          </div>
        </aside>

        {/* Main area */}
        <div className={s.mainArea}>
          <header className={s.topBar}>
            <div className={s.topBarTitle}>
              Super Admin Panel
            </div>
            <div className={s.topBarRight}>
              <span className={s.adminBadge}>Elevated Access</span>
            </div>
          </header>

          <main className={s.content}>
            <Outlet />
          </main>
        </div>
      </div>
    </AdminContext.Provider>
  );
}
